import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, interval, merge } from 'rxjs';
import { map, filter, take, switchMap } from 'rxjs/operators';
import { ITcData, ITcfApi } from 'src/app/core/models/models';
import { EnvironmentService } from 'src/app/environment.service';
import { AARouterURL } from '../../app-routing/enums/routing.enum';

@Injectable({
  providedIn: 'root'
})
export class GoogleTagService {
  private initialized = false;

  triggerInterstitialAd = new Subject();
  triggerInterstitialAd$ = this.triggerInterstitialAd.asObservable();
  servicesEnabled = false;
  refreshCount: Record<string, any> = {};

  private readonly slots = new Map<string, any>();

  private readonly globalTargeting: Record<string, string | string[]> = {
    page: 'routeplanner',
    refresh: '0'
  };
  reTriggerConsentBanner = false;

  constructor(private readonly environmentService: EnvironmentService, private readonly router: Router) {
    const hour = new Date().getHours();
    let timeOfDay;
    if (hour >= 6 && hour < 12) {
      timeOfDay = 'morning';
    } else if (hour >= 12 && hour < 18) {
      timeOfDay = 'afternoon';
    } else if (hour >= 18 && hour < 22) {
      timeOfDay = 'evening';
    } else {
      timeOfDay = 'night';
    }
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayOfWeek = dayNames[new Date().getDay()];
    this.globalTargeting['timeOfDay'] = timeOfDay;
    this.globalTargeting['dayOfWeek'] = dayOfWeek;
    this.globalTargeting['userType'] = sessionStorage.access_token ? 'logged in' : 'user is not authenticated';

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((path) => {
      let page = 'routeplanner';
      if (path.url.includes(AARouterURL.TRAFFIC_NEWS)) {
        page = AARouterURL.TRAFFIC_NEWS;
      } else if (path.url.includes(AARouterURL.MAP_HOTELS)) {
        page = AARouterURL.MAP_HOTELS;
      }
      this.updateGlobalTargeting({
        page,
        origin: '',
        destination: '',
        journeyLength: ''
      });
    });

    this.waitForConsentOnce$().subscribe(() => {
      this.bootstrapGPT();
    });
  }

  private bootstrapGPT(): void {
    if (this.initialized) return;

    globalThis.window.googletag = globalThis.window.googletag || { cmd: [] };

    if (!document.getElementById('gpt-js')) {
      const script = document.createElement('script');
      script.async = true;
      script.id = 'gpt-js';
      script.src = this.environmentService.routeModel.googleAdUrl;
      document.head.appendChild(script);
    }

    globalThis.window.googletag.cmd.push(() => {
      const pubads = globalThis.window.googletag.pubads();

      Object.entries(this.globalTargeting).forEach(([key, value]) => {
        pubads.setTargeting(key, value);
      });

      pubads.enableSingleRequest();

      globalThis.window.googletag.enableServices();

      this.servicesEnabled = true;
      
      const slots = Array.from(this.slots.values());
      if (slots.length) {
        pubads.refresh(slots);
      }
    });
    this.triggerInterstitialAd.next()

    this.initialized = true;
  }

  private withGPT(fn: () => void): void {
    globalThis.window.googletag = globalThis.window.googletag || { cmd: [] };
    globalThis.window.googletag.cmd.push(fn);
  }

  defineSlot(adObj: { id: string; sizes: any; name: any; resolution: any }): void {
    this.withGPT(() => {
      if (this.slots.has(adObj.id)) return;

      const mapping = googletag.sizeMapping();

      if (adObj.sizes) {
        for (const { viewPortSize, adSize } of adObj.sizes) {
          mapping.addSize(viewPortSize, adSize);
        }
      }

      const builtMapping = mapping.build();

      const slot =
        adObj.name === 'routeplanner_interstitial'
          ? globalThis.window.googletag.defineOutOfPageSlot(`/23323315717/${adObj.name}`, adObj.id)
          : globalThis.window.googletag
              .defineSlot(`/23323315717/${adObj.name}`, adObj.resolution, adObj.id)
              .defineSizeMapping(builtMapping);

      if (!slot) return;

      slot.addService(globalThis.window.googletag.pubads());
      this.slots.set(adObj.id, slot);

      globalThis.window.googletag.display(adObj.id);
    });
  }

  refreshSlot(elementId: string): void {
    this.withGPT(() => {
      const slot = this.slots.get(elementId);
      if (!slot) return;

      if (this.refreshCount[elementId]) {
        this.refreshCount[elementId]++;
      } else {
        this.refreshCount[elementId] = 1;
      }
      this.updateGlobalTargeting({ refresh: this.refreshCount[elementId] > 1 ? '2plus' : `${this.refreshCount[elementId]}` });
      globalThis.window.googletag.pubads().refresh([slot]);
    });
  }

  updateGlobalTargeting(targeting: Record<string, string | string[]>): void {
    this.withGPT(() => {
      const pubads = globalThis.window.googletag.pubads();

      Object.entries(targeting).forEach(([key, value]) => {
        if (value) {
          pubads.setTargeting(key, value);
          this.globalTargeting[key] = value;
        }
      });
    });
  }

  destroySlot(elementId: string): void {
    this.withGPT(() => {
      const slot = this.slots.get(elementId);
      if (!slot) return;

      globalThis.window.googletag.destroySlots([slot]);
      this.slots.delete(elementId);
    });
  }

  destroyAll(): void {
    this.withGPT(() => {
      const allSlots = Array.from(this.slots.values());
      if (!allSlots.length) return;

      globalThis.window.googletag.destroySlots(allSlots);
      this.slots.clear();
    });
  }

  waitForConsentOnce$(): Observable<ITcData> {
    const targetEvents = new Set(['tcloaded', 'useractioncomplete']);
    const api$ = interval(100).pipe(
      map(() =>
        typeof globalThis.window !== 'undefined' && typeof globalThis.window.__tcfapi === 'function'
          ? (globalThis.window.__tcfapi as ITcfApi)
          : undefined
      ),
      filter((api): api is ITcfApi => !!api),
      take(1)
    );

    const getTcData$ = (api: ITcfApi) =>
      new Observable<ITcData>((sub) => {
        api('getTCData', 2, (tcData, success) => {
          success ? sub.next(tcData) : sub.error(new Error('getTCData failed'));
          sub.complete();
        });
      });

    const removeListener = (api: ITcfApi) => {
      if (listenerId != null) {
        api('removeEventListener', 2, () => {}, listenerId);
      }
    };

    let listenerId: number | null = null;
    const tcfEvents$ = (api: ITcfApi) =>
      new Observable<ITcData>((sub) => {
        api('addEventListener', 2, (tcData, success) => {
          if (!success) return;
          listenerId = tcData.listenerId ?? null;
          sub.next(tcData);
        });

        return removeListener(api);
      });

    return api$.pipe(
      switchMap((api) => merge(getTcData$(api), tcfEvents$(api))),
      filter((tc) => targetEvents.has(tc.eventStatus)),
      take(1)
    );
  }
}
